import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('albmove_token'));

  const normalizeRole = (rawRole, clientType) => {
    // If client_type is business, always return BUSINESS
    if (clientType && String(clientType).trim().toLowerCase() === 'business') {
      return 'BUSINESS';
    }
    
    const role = String(rawRole || '').trim().toUpperCase();
    if (role === 'SUPER_ADMIN' || role === 'SUPERADMIN') return 'ADMIN';
    if (role === 'BUSINESS') return 'BUSINESS';
    if (role === 'MODERATOR') return 'MODERATOR';
    if (role === 'ADMIN') return 'ADMIN';
    if (role === 'SYSTEM') return 'SYSTEM';
    return 'USER';
  };

  const normalizeUser = (rawUser) => {
    if (!rawUser) return rawUser;
    // Pass client_type to normalizeRole so business accounts get BUSINESS role
    const clientType = rawUser.client_type || rawUser.account_type;
    const normalizedRole = normalizeRole(rawUser.role, clientType);
    return { ...rawUser, role: normalizedRole, client_type: clientType };
  };

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('albmove_token');
      // Check both localStorage and sessionStorage for user
      const savedUser = localStorage.getItem('albmove_user') || sessionStorage.getItem('albmove_user');
      let parsedSavedUser = null;
      try {
        parsedSavedUser = savedUser ? JSON.parse(savedUser) : null;
      } catch {
        parsedSavedUser = null;
      }
      
      if (savedToken) {
        try {
          const response = await getMe();
          const normalizedFromApi = normalizeUser(response.data);
          setUser(normalizedFromApi);
          setToken(savedToken);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (userData, authToken, rememberMe = true) => {
    // If remember me is checked, use localStorage; otherwise use sessionStorage
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Always save to localStorage for token (for API calls)
    localStorage.setItem('albmove_token', authToken);
    const normalizedUser = normalizeUser(userData);
    storage.setItem('albmove_user', JSON.stringify(normalizedUser));
    
    // Save persistence preference
    localStorage.setItem('albmove_persist', rememberMe ? 'true' : 'false');
    
    setToken(authToken);
    setUser(normalizedUser);
    return normalizedUser;
  };

  const logout = () => {
    localStorage.removeItem('albmove_token');
    localStorage.removeItem('albmove_user');
    localStorage.removeItem('albmove_persist');
    sessionStorage.removeItem('albmove_user');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'ADMIN';
  const isModerator = () => user?.role === 'MODERATOR';
  const isBusiness = () => user?.role === 'BUSINESS';
  const isUser = () => user?.role === 'USER';

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout,
      isAdmin,
      isModerator,
      isBusiness,
      isUser,
      isAuthenticated: !!token 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
